import { clientAgentId } from 'constants/history';
import { HistoryRequestType } from 'enums/HistoryRequestType';
import { TransmitStatus } from 'enums/TransmitStatus';
import { ILastAgent } from 'interfaces/LastAgent';
import { RenderMessageData } from 'interfaces/MessageData';
import { UnreadReaction } from 'interfaces/UnreadReactions';
import { Action } from 'redux';
import { _counterLog } from 'utils/_counterLog';
import { mergeBubbles } from 'utils/bubbles';
import { convertBackendTimestamp } from 'utils/dateTime';
import { messageToRenderData, msgStatus } from 'utils/messages';

import {
    AddClientSideMessageAction,
    DeleteClientSideMessageAction,
    FocusMessageAction,
    HistoryActionType,
    InitialisedAction,
    MarkMessageFavoriteAction,
    MarkMessageReceivedAction,
    PendingMessagesLoadedAction,
    PrependFollowedMessageAction,
    PruneFollowedMessagesAction,
    ReceiveHistoryAction,
    ReceiveMessageAction,
    ReplyMessageAction,
    SendMessageReadAction,
    SendMessageTimeoutAction,
    SetAppealClosedBy,
    SetFavoritesModeAction,
    SetFirstUnreadMsgIdAction,
    SetHistoryBeginningAction,
    SetHistoryEndAction,
    SetLastAgentAction,
    SetLastMessageSentTimestampAction,
    SetMessageReactionsAction,
    SetMessagesReceivedOnlineIdListAction,
    SetOperatorAvailableAction,
    SetOperatorAwaitTimeAction,
    SetTextSelectionMessageIdAction,
    SetUnreadReactionsAction,
    SetVisibleHistoryRangeAction,
    UnmarkMessageFavoriteAction,
} from './history.actions';

const updateToReceived = {
    [TransmitStatus.Sent]: true,
};

const updateToRead = {
    [TransmitStatus.Sent]: true,
    [TransmitStatus.Delivered]: true,
};

export const getIsAtBottom = ({
    msgList,
    overrideOffset = 150,
}: {
    msgList?: HTMLElement;
    overrideOffset?: number;
}) => {
    if (msgList) {
        const listHeight = msgList.clientHeight;
        const listScrollHeight = msgList.scrollHeight;
        const listScrollTop = msgList.scrollTop;

        return listScrollHeight - listScrollTop - listHeight < overrideOffset;
    }

    return true;
};

const getIsCloseToBottom = (
    { bubbles, currentBubbleIndex, historyEndMsgId }: typeof initialState,
    visibleRange: typeof initialState.visibleRange,
    msgList?: HTMLDivElement,
): {
    isCloseToBottom: boolean;
    isAtBottom: boolean;
} => {
    const isAtBottom = getIsAtBottom({ msgList, overrideOffset: 24 });

    if (visibleRange[1] === '0') {
        return { isCloseToBottom: true, isAtBottom };
    }

    const lastBubbleIndex = bubbles.length - 1;

    if (
        currentBubbleIndex !== lastBubbleIndex ||
        historyEndMsgId === String(Number.MAX_SAFE_INTEGER)
    ) {
        return { isCloseToBottom: false, isAtBottom };
    }

    const curBubble = bubbles?.[currentBubbleIndex];
    const msgNearBottomIndex = (curBubble?.length ?? 0) - 4;
    const msgNearBottom = msgNearBottomIndex >= 0 && curBubble[msgNearBottomIndex];

    return {
        isCloseToBottom: !msgNearBottom || parseInt(visibleRange[1]) > parseInt(msgNearBottom),
        isAtBottom,
    };
};

const initialState = {
    initialised: false,
    loading: true,
    chatNow: null as boolean,
    operatorAvailable: null as boolean,
    followedMessageIds: [] as string[],
    clientSideMessages: {} as { [uuid: string]: RenderMessageData },
    messages: {} as { [msgId: string]: RenderMessageData },
    bubbles: [] as string[][],
    currentBubbleIndex: 0,
    lastReadMessageId: '0',
    lastReceivedMessageId: '0',
    lastReadByClientMessageId: '0',
    historyBeginningMsgId: '0',
    historyEndMsgId: String(Number.MAX_SAFE_INTEGER),
    msgToFocus: null as { msgId: string; smooth: boolean },
    visibleRange: [Number.MAX_SAFE_INTEGER.toString(), '0' as string] as const,
    lastHistoryReceiveTimestamp: null as Date,
    lastMessageSentTimestamp: null as Date,
    lastMessageReceivedTimestamp: null as Date,
    isCloseToBottom: true,
    isAtBottom: true,
    firstUnreadMsgId: null as string,
    lastAgent: undefined as ILastAgent,
    appealClosedBy: undefined as ILastAgent,
    client: null as { id: number },
    favoritesMode: false,
    operatorAwaitTimeMs: null as number | null,
    textSelectionMessageId: null as string | null,
    unreadReactions: [] as Array<UnreadReaction>,
    messagesReceivedOnlineIds: [] as string[],
};

export const historyReducer = (
    state = initialState,
    action: Action<HistoryActionType>,
): typeof initialState => {
    switch (action.type) {
        case HistoryActionType.Initialised:
            const initialised = (action as InitialisedAction).payload;
            return {
                ...state,
                initialised,
            };

        case HistoryActionType.SetOperatorAvailable:
            const { operatorAvailable, chatNow } = (action as SetOperatorAvailableAction).payload;
            return {
                ...state,
                operatorAvailable,
                chatNow,
            };

        case HistoryActionType.SetOperatorAwaitTimeMs:
            const operatorAwaitTimeMs = (action as SetOperatorAwaitTimeAction).payload;
            return {
                ...state,
                operatorAwaitTimeMs,
            };

        case HistoryActionType.RequestHistory:
            return {
                ...state,
                loading: true,
            };

        case HistoryActionType.SetTextSelectionMessageId:
            const { messageId } = (action as SetTextSelectionMessageIdAction).payload;

            if (messageId && state.textSelectionMessageId) {
                return { ...state };
            }

            return {
                ...state,
                textSelectionMessageId: messageId,
            };

        case HistoryActionType.ReceiveHistory: {
            const { history, timestamp } = (action as ReceiveHistoryAction).payload;
            const newMessages: { [msgId: string]: RenderMessageData } = {};
            const newMsgIds = Object.keys(history.messages);

            for (const msgId of newMsgIds) {
                const newMsg = messageToRenderData(history.messages[msgId]);
                newMsg.status = msgStatus(
                    msgId,
                    history.lastReadMessageId,
                    history.lastReceivedMessageId,
                );
                newMessages[msgId] = newMsg;
            }

            let { historyBeginningMsgId, historyEndMsgId } = state;
            const firstMsgId = newMsgIds[0];
            const lastMsgId = newMsgIds.at(-1);

            let { lastMessageSentTimestamp, lastMessageReceivedTimestamp } = state;

            // History was requested (or pushed) from bottom, last page
            if (!history.requestedMsgId) {
                historyEndMsgId = lastMsgId;

                const newMessagesArray = Object.values(newMessages);

                for (let i = newMessagesArray.length - 1; i >= 0; i--) {
                    const msg = newMessagesArray[i];

                    if (msg.agentId === clientAgentId && !lastMessageSentTimestamp) {
                        lastMessageSentTimestamp = new Date(convertBackendTimestamp(msg.time));
                    } else if (!lastMessageReceivedTimestamp) {
                        lastMessageReceivedTimestamp = new Date(convertBackendTimestamp(msg.time));
                    }

                    if (lastMessageSentTimestamp && lastMessageReceivedTimestamp) break;
                }
            }

            // History page request returned no new messages
            if (newMsgIds.length === 1) {
                if (history.type !== HistoryRequestType.Older) {
                    historyEndMsgId = lastMsgId;
                }

                if (history.type !== HistoryRequestType.Newer) {
                    historyBeginningMsgId = firstMsgId;
                }
            }

            return {
                ...state,
                loading: false,
                operatorAvailable: history.operatorAvailable,
                chatNow: history.chatNow,
                lastReadMessageId: history.lastReadMessageId || state.lastReadMessageId,
                lastReceivedMessageId: history.lastReceivedMessageId || state.lastReceivedMessageId,
                messages: {
                    ...state.messages,
                    ...newMessages,
                },
                bubbles: mergeBubbles(state.bubbles, newMsgIds),
                historyBeginningMsgId,
                historyEndMsgId,
                lastHistoryReceiveTimestamp: timestamp,
                lastMessageSentTimestamp,
                lastMessageReceivedTimestamp,
                client: history.client ?? null,
            };
        }

        case HistoryActionType.SetHistoryBeginning:
            const historyBeginningMsgId = (action as SetHistoryBeginningAction).payload;
            return {
                ...state,
                historyBeginningMsgId,
            };

        case HistoryActionType.SetHistoryEnd:
            const historyEndMsgId = (action as SetHistoryEndAction).payload;
            return {
                ...state,
                historyEndMsgId,
            };

        case HistoryActionType.GoToBottom:
            return {
                ...state,
                currentBubbleIndex: Math.max(0, state.bubbles.length - 1),
            };

        case HistoryActionType.AddClientSideMessage: {
            const { message } = (action as AddClientSideMessageAction).payload;
            return {
                ...state,
                clientSideMessages: {
                    ...state.clientSideMessages,
                    [message.uuid]: messageToRenderData(message),
                },
            };
        }

        case HistoryActionType.SetLastMessageSentTimestamp:
            const lastMessageSentTimestamp = (action as SetLastMessageSentTimestampAction).payload;

            return {
                ...state,
                lastMessageSentTimestamp,
            };

        case HistoryActionType.SendMessageTimeout:
            const timedOutUuid = (action as SendMessageTimeoutAction).payload;
            const timedOutMsg = state.clientSideMessages[timedOutUuid];

            if (!timedOutMsg) {
                return state;
            }

            return {
                ...state,
                clientSideMessages: {
                    ...state.clientSideMessages,
                    [timedOutUuid]: {
                        ...timedOutMsg,
                        status: TransmitStatus.Error,
                    },
                },
            };

        case HistoryActionType.DeleteClientSideMessage:
            const clientSideMsgUuid = (action as DeleteClientSideMessageAction).payload;
            if (state.clientSideMessages[clientSideMsgUuid]) {
                const clientSideMessages = { ...state.clientSideMessages };
                delete clientSideMessages[clientSideMsgUuid];

                return {
                    ...state,
                    clientSideMessages,
                };
            }

            return state;

        case HistoryActionType.PendingMessagesLoaded:
            const pendingMessages = (action as PendingMessagesLoadedAction).payload;
            return {
                ...state,
                clientSideMessages: {
                    ...state.clientSideMessages,
                    ...pendingMessages,
                },
            };

        case HistoryActionType.ReceiveMessage:
            const receivedMsg = (action as ReceiveMessageAction).payload;
            const newMsg = messageToRenderData(receivedMsg);
            newMsg.status = TransmitStatus.Sent;
            _counterLog.log(`HistoryActionType.ReceiveMessage`, `msg.id => ${newMsg.id}`);

            const newBubbles = [...state.bubbles];
            if (newBubbles.length === 0 || state.historyEndMsgId === initialState.historyEndMsgId) {
                newBubbles.push([]);
            }

            newBubbles[newBubbles.length - 1] = [...newBubbles.at(-1), newMsg.id];

            let { clientSideMessages } = state;
            const clientSideMsg = clientSideMessages[newMsg.uuid];

            if (clientSideMsg) {
                clientSideMessages = { ...clientSideMessages };
                delete clientSideMessages[newMsg.uuid];
            }

            const newState: typeof initialState = {
                ...state,
                messages: {
                    ...state.messages,
                    [receivedMsg.id]: newMsg,
                },
                bubbles: newBubbles,
                historyEndMsgId: receivedMsg.id,
                clientSideMessages,
            };

            if (receivedMsg.agentId === clientAgentId) {
                newState.lastMessageSentTimestamp = new Date();
            } else {
                newState.lastMessageReceivedTimestamp = new Date();
            }

            return newState;

        case HistoryActionType.ReplyMessage: {
            const receivedMsg = (action as ReplyMessageAction).payload;
            const msgId = receivedMsg.id;

            const { messages, historyEndMsgId, lastReadByClientMessageId } = state;
            if (typeof messages[msgId] !== 'undefined') {
                return state;
            }

            const newMessages: { [msgId: string]: RenderMessageData } = {};
            const newMsg = messageToRenderData(receivedMsg);
            newMsg.status = msgStatus(msgId, lastReadByClientMessageId, historyEndMsgId);
            newMessages[msgId] = newMsg;

            const newState: typeof initialState = {
                ...state,
                messages: {
                    ...state.messages,
                    ...newMessages,
                },
                bubbles: mergeBubbles(state.bubbles, [msgId]),
            };

            return newState;
        }

        case HistoryActionType.MarkMessageReceived:
            const receivedId = (action as MarkMessageReceivedAction).payload;
            return {
                ...state,
                messages: Object.fromEntries(
                    Object.entries(state.messages).map(([msgId, msg]) => [
                        msgId,
                        updateToReceived[msg.status] && msgId <= receivedId
                            ? {
                                  ...msg,
                                  status: TransmitStatus.Delivered,
                              }
                            : msg,
                    ]),
                ),
            };

        case HistoryActionType.MarkMessageRead:
            const readId = (action as MarkMessageReceivedAction).payload;
            return {
                ...state,
                messages: Object.fromEntries(
                    Object.entries(state.messages).map(([msgId, msg]) => [
                        msgId,
                        updateToRead[msg.status] && msgId <= readId
                            ? {
                                  ...msg,
                                  status: TransmitStatus.Read,
                              }
                            : msg,
                    ]),
                ),
            };

        case HistoryActionType.SendMessageRead:
            const lastReadByClientMessageId = (action as SendMessageReadAction).payload;
            _counterLog.log(
                `HistoryActionType.SendMessageRead`,
                `lastReadByClientMessageId => ${lastReadByClientMessageId}`,
            );
            return {
                ...state,
                lastReadByClientMessageId,
            };

        case HistoryActionType.FocusMessage:
            const msgToFocus = (action as FocusMessageAction).payload;

            let currentBubbleIndex = state.bubbles.findIndex((b) => b.includes(msgToFocus.msgId));

            if (currentBubbleIndex === -1) {
                currentBubbleIndex = Math.max(0, state.bubbles.length - 1);
            }

            return {
                ...state,
                msgToFocus,
                currentBubbleIndex,
            };

        case HistoryActionType.MessageFocused:
            return {
                ...state,
                msgToFocus: null,
            };

        case HistoryActionType.SetVisibleRange:
            const { visibleRange, msgList } = (action as SetVisibleHistoryRangeAction).payload;
            return {
                ...state,
                visibleRange,
                ...getIsCloseToBottom(state, visibleRange, msgList),
            };

        case HistoryActionType.SetMessageReactions: {
            const { messageId, reactions } = (action as SetMessageReactionsAction).payload;
            const message = state.messages[messageId];

            return {
                ...state,
                messages: {
                    ...state.messages,
                    [messageId]: {
                        ...message,
                        reactions,
                    },
                },
            };
        }

        case HistoryActionType.MarkMessageFavorite: {
            const { msgId } = (action as MarkMessageFavoriteAction).payload;
            const msg = state.messages[msgId];

            return {
                ...state,
                messages: {
                    ...state.messages,
                    [msgId]: {
                        ...msg,
                        favorite: true,
                    },
                },
            };
        }

        case HistoryActionType.UnmarkMessageFavorite: {
            const { msgId } = (action as UnmarkMessageFavoriteAction).payload;
            const msg = state.messages[msgId];

            return {
                ...state,
                messages: {
                    ...state.messages,
                    [msgId]: {
                        ...msg,
                        favorite: false,
                    },
                },
            };
        }

        case HistoryActionType.Clear: {
            const {
                initialised,
                followedMessageIds,
                messages,
                bubbles,
                currentBubbleIndex,
                historyBeginningMsgId,
                historyEndMsgId,
                msgToFocus,
                visibleRange,
                isCloseToBottom,
                lastAgent,
                client,
                firstUnreadMsgId,
            } = initialState;

            return {
                ...state,
                initialised,
                followedMessageIds,
                messages,
                bubbles,
                currentBubbleIndex,
                historyBeginningMsgId,
                historyEndMsgId,
                msgToFocus,
                visibleRange,
                isCloseToBottom,
                lastAgent,
                client,
                firstUnreadMsgId,
            };
        }

        case HistoryActionType.SetFirstUnreadMsgId:
            const firstUnreadMsgId = (action as SetFirstUnreadMsgIdAction).payload;
            return {
                ...state,
                firstUnreadMsgId,
            };

        case HistoryActionType.SetLastAgent: {
            const lastAgent = (action as SetLastAgentAction).payload;
            return {
                ...state,
                lastAgent,
            };
        }

        case HistoryActionType.ClearLastAgent:
            return {
                ...state,
                lastAgent: null,
            };

        case HistoryActionType.SetAppealClosedBy:
            const lastAgent = (action as SetAppealClosedBy).payload;

            return {
                ...state,
                appealClosedBy: lastAgent || state.appealClosedBy,
            };

        case HistoryActionType.SetFavoritesMode: {
            const favoritesMode = (action as SetFavoritesModeAction).payload;
            return {
                ...state,
                favoritesMode,
            };
        }

        case HistoryActionType.PrependFollowedMessage: {
            const messageId = (action as PrependFollowedMessageAction).payload;
            const followedMessageIds = state.followedMessageIds.reduce((acc, id) => {
                if (Number(id) > Number(messageId)) {
                    acc.push(id);
                }
                return acc;
            }, []);
            return {
                ...state,
                followedMessageIds: [messageId, ...followedMessageIds],
            };
        }

        case HistoryActionType.PruneFollowedMessages: {
            const { end } = (action as PruneFollowedMessagesAction).payload;
            const followedMessageIds = [...state.followedMessageIds];
            followedMessageIds.splice(0, end);

            return {
                ...state,
                followedMessageIds: followedMessageIds,
            };
        }

        case HistoryActionType.SetUndreadReactions: {
            const reactions = (action as SetUnreadReactionsAction).payload;

            return {
                ...state,
                unreadReactions: reactions,
            };
        }

        case HistoryActionType.SetMessagesReceivedOnlineIdList: {
            const idList = (action as SetMessagesReceivedOnlineIdListAction).payload;

            return {
                ...state,
                messagesReceivedOnlineIds: idList,
            };
        }

        default:
            return state;
    }
};
